import { useNarrow } from '../../lib/use-narrow';
import { useWorkspaces } from '../../lib/workspaces';
import { RoutinesSummary } from './routines/RoutinesSummary';
import { TasksSummary } from './tasks/TasksSummary';
import { GoalsSummary } from './goals/GoalsSummary';
import { FilesSummaryCard } from './agents/FilesSummaryCard';
import { AppsSummaryCard } from './agents/AppsSummaryCard';
import { CalendarSummaryCard } from './calendar/CalendarSummaryCard';
import { useCalendarGate } from '../../lib/calendar-flag';
import { useSummaryTilesReady } from './agents/use-summary-tiles-ready';
import { useSpaceCardEnabled } from '../../lib/space-card-flag';
import { ScrollArea } from './ui';
import home3 from './home3/Home3Categories.module.css';
import styles from './WorkspaceSummaryRow.module.css';

// The summary tiles for one Space: THE SAME components Your Agents renders account-wide, given a
// `workspaceId`, so the two surfaces cannot answer the same question differently.
//
// Two presentations, one per viewport — the same split Your Agents uses (Figma 15311:197455 ≡ the
// agents-page cards, scoped to a Space):
//   • narrow (<720px) — the compact single-row entry tiles with 12px icon-count badges (`entryRow`),
//     grouped Tasks → Goals → Routines → Files → Apps, matching the phones.
//   • wide — the desktop heading-over-badges tiles, in the SAME order (Figma `15378:117541`),
//     reflowing to fewer columns as the width drops.
//
// Each tile links to its own screen pre-filtered to this Space, so the drill-in never widens the scope
// the reader is already in.
//
// Under the Space card (`symphonySpaceCardV2`, spec 2026-10-29) the row is the Space's CATEGORIES at every width: Home's
// icon tiles (`SpaceCategoryTile`) in Home's slider, Tasks → Goals → Routines → Files → Apps → Calendar, each with what
// is new there as its disc.
export function WorkspaceSummaryRow({ slug, page }: { slug: string; page: string }) {
  const narrow = useNarrow();
  const workspaces = useWorkspaces();
  const workspace = workspaces.status === 'ready' ? workspaces.workspaces.find((w) => w.slug === slug) : undefined;
  // Above the early return, as hook order demands. It reads only whether the queries are in flight, so
  // the momentary unscoped call before the roster lands cannot put an account-wide COUNT on screen.
  const tilesReady = useSummaryTilesReady(workspace?.id);
  const calendar = useCalendarGate().enabled;
  const spaceCard = useSpaceCardEnabled();
  // Until the roster resolves there is no id to scope by, and an unscoped read here would briefly show
  // the ACCOUNT's counts inside a Space — a wrong number is worse than a late one.
  if (!workspace) return null;
  // And the row appears as one complete line, not tile by tile — see `useSummaryTilesReady`.
  if (!tilesReady) return null;

  const scope = { workspaceId: workspace.id, workspaceSlug: workspace.slug, pageName: page } as const;
  if (spaceCard) {
    return (
      <ScrollArea axis="horizontal" bar="hidden" className={`${home3.scroller} ${styles.categories}`}>
        <div className={home3.row} data-testid="spaceCategories">
          <TasksSummary tile {...scope} />
          <GoalsSummary tile {...scope} />
          <RoutinesSummary tile {...scope} />
          <FilesSummaryCard tile {...scope} />
          <AppsSummaryCard tile {...scope} />
          {calendar && <CalendarSummaryCard tile {...scope} />}
        </div>
      </ScrollArea>
    );
  }
  if (narrow) {
    return (
      <div className={styles.entryCards}>
        <TasksSummary entryRow {...scope} />
        <GoalsSummary entryRow {...scope} />
        <RoutinesSummary entryRow {...scope} />
        <FilesSummaryCard entryRow {...scope} />
        <AppsSummaryCard entryRow {...scope} />
        {calendar && <CalendarSummaryCard entryRow {...scope} />}
      </div>
    );
  }

  return (
    <div className={styles.row}>
      <TasksSummary {...scope} />
      <GoalsSummary {...scope} />
      <RoutinesSummary {...scope} />
      <FilesSummaryCard {...scope} />
      <AppsSummaryCard {...scope} />
      {calendar && <CalendarSummaryCard {...scope} />}
    </div>
  );
}
